'use client';

import React from 'react';
import { MoreVertical } from 'lucide-react';
import { cn } from '../lib/utils';
import { Breadcrumb, type BreadcrumbItem, type BreadcrumbProps } from './breadcrumb';
import { Button, buttonVariants } from './button';
import { DropdownMenu, type DropdownMenuItem } from './dropdown-menu';
import { AboutThisPage } from './about-this-page';
import { PageTitle } from './typography';
import { Sheet } from './sheet';
import { TechnicalValue } from './technical-value';
import { SectionTabsSlot } from './sub-page-tabs/sub-page-tabs';
import { withDisabledReason } from './disabled-action';
import {
  CustomisePage,
  CustomisePageIcon,
  type CustomisePageProps,
  isPageItemVisible,
} from './customise-page';
import { usePageLayoutPreferences } from '../hooks/use-page-layout-preferences';

export type PageHeaderAction = Pick<
  DropdownMenuItem,
  'label' | 'href' | 'onClick' | 'icon' | 'disabled' | 'disabledReason' | 'destructive'
>;

export interface PageHeaderProps {
  title: string;
  /** Record identity, usually an initials Avatar. */
  avatar?: React.ReactNode;
  /** Keep record context actions beside the primary action on phones. */
  showSecondaryOnMobile?: boolean;
  /** A short, single-sentence purpose; wraps rather than truncates at enlarged text sizes. */
  description?: React.ReactNode;
  meta?: React.ReactNode;
  /** Labelled record references, displayed after contextual metadata. */
  identifiers?: ReadonlyArray<{ label: string; value: string }>;
  status?: React.ReactNode;
  /** Caller-formatted tenant date and actor; omitted when unknown. */
  updated?: { at: string; by?: string };
  about?: React.ReactNode;
  aboutInOverflow?: boolean;
  actions?: {
    primary?: React.ReactNode;
    secondary?: PageHeaderAction[];
    overflow?: PageHeaderAction[];
  };
  /** Supply only for nested records. The last item identifies the current page. */
  breadcrumbs?: BreadcrumbItem[];
  /** Preserve client-side navigation when the host app supplies its link component. */
  breadcrumbLinkRender?: BreadcrumbProps['renderLink'];
  className?: string;
  /** Reserve the tab row during hydration when this page always has section navigation. */
  reserveSectionNavigation?: boolean;
  /** One Customise page entry in the header overflow at every width. */
  customise?: Omit<CustomisePageProps, 'open' | 'onClose'>;
}

function SecondaryAction({ action }: { action: PageHeaderAction }) {
  const variant = action.destructive ? 'danger' : 'secondary';
  if (action.href && !action.disabled) {
    return (
      <a data-ui="button" href={action.href} className={buttonVariants({ variant })}>
        {action.icon}
        {action.label}
      </a>
    );
  }
  return (
    <Button
      type="button"
      variant={variant}
      disabled={action.disabled}
      disabledReason={
        action.disabled
          ? (action.disabledReason ?? 'This action is currently unavailable.')
          : undefined
      }
      onClick={action.onClick}
    >
      {action.icon}
      {action.label}
    </Button>
  );
}

/** Unboxed header: title/context and actions share the desktop row. */
export function PageHeader({
  title,
  avatar,
  showSecondaryOnMobile = false,
  description,
  meta,
  identifiers,
  status,
  updated,
  about,
  aboutInOverflow = false,
  actions,
  breadcrumbs,
  breadcrumbLinkRender,
  className,
  reserveSectionNavigation = false,
  customise,
}: PageHeaderProps) {
  const [aboutOpen, setAboutOpen] = React.useState(false);
  const [customiseOpen, setCustomiseOpen] = React.useState(false);
  const overflowTrigger = React.useRef<HTMLButtonElement | null>(null);
  const fallback = usePageLayoutPreferences(
    customise?.scope ?? { userId: '', tenantId: '', pageId: '' },
    'visibility',
    Boolean(customise && !customise.preferences),
  );
  const preferences = customise?.preferences ?? fallback;
  const hidden =
    customise?.items.filter((item) => !isPageItemVisible(item, preferences.values)).length ?? 0;
  const secondary = (actions?.secondary ?? []).filter(
    (action) => !breadcrumbs?.length || !/^back to (?:.* )?list$/i.test(action.label),
  );
  const customiseAction: PageHeaderAction[] = customise
    ? [
        {
          label: `Customise page · ${preferences.ready ? hidden : '…'} hidden`,
          icon: <CustomisePageIcon aria-hidden="true" className="size-4" />,
          onClick: () => {
            // The menu item unmounts as the Sheet opens; preserve a durable return target.
            overflowTrigger.current?.focus();
            setCustomiseOpen(true);
          },
        },
      ]
    : [];
  const explainAction = (action: PageHeaderAction) =>
    action.disabled
      ? {
          ...action,
          disabledReason: action.disabledReason ?? 'This action is currently unavailable.',
        }
      : action;
  const extraActions = [
    ...(actions?.overflow ?? []),
    ...(aboutInOverflow && about
      ? [
          {
            label: 'About this page',
            onClick: () => {
              overflowTrigger.current?.focus();
              setAboutOpen(true);
            },
          },
        ]
      : []),
  ];
  const overflow = [...secondary.slice(2), ...extraActions, ...customiseAction].map(explainAction);
  const phoneOverflow =
    !showSecondaryOnMobile && (secondary.length > 0 || extraActions.length > 0 || customise) ? (
      <DropdownMenu
        className="shrink-0 sm:hidden"
        trigger={
          <Button
            type="button"
            variant="secondary"
            size="icon"
            aria-label="More actions"
            onClick={(event) => {
              overflowTrigger.current = event.currentTarget;
            }}
          >
            <MoreVertical aria-hidden="true" />
          </Button>
        }
        items={[...secondary, ...extraActions, ...customiseAction].map(explainAction)}
      />
    ) : null;
  return (
    <header className={cn('min-w-0 space-y-4 break-normal [overflow-wrap:break-word]', className)}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumb items={breadcrumbs} renderLink={breadcrumbLinkRender} />
      )}
      <div className="flex min-w-0 flex-wrap items-start gap-x-4 gap-y-3">
        <div className="min-w-0 flex-[1_1_16rem] space-y-2">
          <div className="flex min-w-0 flex-wrap items-start gap-x-4 gap-y-3">
            <div className="flex min-w-0 flex-[1_1_16rem] flex-wrap items-center gap-2">
              {avatar}
              <PageTitle className="min-w-0 flex-[1_1_16rem] break-normal [overflow-wrap:break-word]">
                {title}
              </PageTitle>
              {status != null && <div className="max-w-full">{status}</div>}
            </div>
            {phoneOverflow && (
              <div className="ml-auto flex min-w-0 flex-[0_1_auto] flex-wrap justify-start gap-2 sm:hidden">
                {phoneOverflow}
              </div>
            )}
          </div>
          {description && (
            <p className="min-w-0 break-words font-body text-body text-text-muted [overflow-wrap:anywhere]">
              {description}
            </p>
          )}
          {(meta != null || updated) && (
            <div className="flex min-w-0 flex-wrap items-center gap-2 font-body text-body text-text-muted">
              {meta != null && <div className="min-w-0 max-w-full">{meta}</div>}
              {updated?.at && (
                <p className="w-full">
                  Updated {updated.at}
                  {updated.by ? ` by ${updated.by}` : ''}
                </p>
              )}
            </div>
          )}
          {identifiers && identifiers.length > 0 && (
            <div className="flex min-w-0 flex-wrap gap-x-4 gap-y-2">
              {identifiers.map(({ label, value }) => (
                <div
                  key={`${label}:${value}`}
                  className="flex min-w-0 max-w-full flex-wrap items-center gap-2"
                >
                  <span className="min-w-0 break-words font-body text-caption text-text-muted [overflow-wrap:anywhere]">
                    {label}
                  </span>
                  <TechnicalValue label={label} value={value} />
                </div>
              ))}
            </div>
          )}
          {about != null && !aboutInOverflow && (
            <div data-page-about className="w-full min-w-0">
              <AboutThisPage>{about}</AboutThisPage>
            </div>
          )}
        </div>
        {(actions?.primary != null ||
          secondary.length > 0 ||
          extraActions.length > 0 ||
          customise) && (
          <div
            className={cn(
              'ml-auto min-w-0 max-w-full basis-full flex-wrap items-center justify-start gap-2 sm:flex sm:flex-[0_1_auto]',
              actions?.primary != null || showSecondaryOnMobile ? 'flex' : 'hidden',
              showSecondaryOnMobile && 'sm:flex-nowrap',
            )}
          >
            <div
              className={cn(
                'min-w-0 max-w-full flex-wrap items-center gap-2 sm:flex',
                showSecondaryOnMobile ? 'flex' : 'hidden',
              )}
            >
              {secondary.slice(0, 2).map((action) => (
                <SecondaryAction key={action.label} action={action} />
              ))}
              {overflow.length > 0 && (
                <DropdownMenu
                  trigger={
                    <Button
                      type="button"
                      variant="secondary"
                      size="icon"
                      aria-label="More actions"
                      onClick={(event) => {
                        overflowTrigger.current = event.currentTarget;
                      }}
                    >
                      <MoreVertical aria-hidden="true" />
                    </Button>
                  }
                  items={overflow}
                />
              )}
            </div>
            {actions?.primary != null && (
              <div
                className={cn(
                  'flex min-w-0 max-w-full flex-1 flex-wrap gap-2 [overflow-wrap:normal] [&>*]:w-full [&>*]:justify-start [&>*]:text-left sm:flex-none sm:[&>*]:w-auto sm:[&>*]:justify-center sm:[&>*]:text-center',
                  showSecondaryOnMobile && 'order-first basis-full sm:basis-auto',
                )}
              >
                {withDisabledReason(actions.primary)}
              </div>
            )}
          </div>
        )}
      </div>
      <SectionTabsSlot reserveSpace={reserveSectionNavigation} />
      {aboutInOverflow && about ? (
        <Sheet open={aboutOpen} onClose={() => setAboutOpen(false)} title="About this page">
          {about}
        </Sheet>
      ) : null}
      {customise && (
        <CustomisePage
          {...customise}
          open={customiseOpen}
          onClose={() => setCustomiseOpen(false)}
        />
      )}
    </header>
  );
}
